<script setup>
import { computed, ref } from 'vue';
import { useTireSetStore } from '@/stores/tireSet';
import { storeToRefs } from 'pinia';

const emit = defineEmits(['close']);

const tireSetStore = useTireSetStore();
const { current } = storeToRefs(tireSetStore);

const noteMessage = ref('');
const noteTarget = ref(null);
const savingNote = ref(false);
const photoInput = ref(null);
const uploadingPhoto = ref(false);

const tireTargetOptions = computed(() => {
    return (current.value?.tires ?? []).map((tire) => ({
        label: `${tire.position} · ${tire.barcode}`,
        value: tire.barcode,
    }));
});

const submitNote = async () => {
    if (!noteMessage.value.trim() || savingNote.value) {
        return;
    }
    savingNote.value = true;
    try {
        await tireSetStore.addNote(current.value.hash, {
            message: noteMessage.value,
            tire_barcode: noteTarget.value,
        });
        noteMessage.value = '';
        noteTarget.value = null;
    } catch (error) {
        // The response interceptor surfaces the error toast
    }
    savingNote.value = false;
};

const removeNote = async (note) => {
    try {
        await tireSetStore.deleteNote(current.value.hash, note.hash);
    } catch (error) {
        // The response interceptor surfaces the error toast
    }
};

const pickPhoto = () => {
    photoInput.value?.click();
};

const onPhotoChosen = async (event) => {
    const file = event.target.files?.[0];
    if (!file || uploadingPhoto.value) {
        return;
    }
    uploadingPhoto.value = true;
    const formData = new FormData();
    formData.append('photo', file);
    try {
        await tireSetStore.uploadPhoto(current.value.hash, formData);
    } catch (error) {
        // The response interceptor surfaces the error toast
    }
    uploadingPhoto.value = false;
    event.target.value = '';
};

const removePhoto = async (photo) => {
    try {
        await tireSetStore.deletePhoto(current.value.hash, photo.hash);
    } catch (error) {
        // The response interceptor surfaces the error toast
    }
};

const noteMeta = (note) => [note.author, formatDate(note.created_at)].filter(Boolean).join(' · ');

const seasonColor = (season) => {
    if (season === 'winter') {
        return 'winter';
    }
    if (season === 'summer') {
        return 'summer';
    }
    return 'neutral';
};

const seasonLabel = computed(() => {
    if (!current.value?.season) {
        return '';
    }
    return current.value.season;
});

const formatDate = (iso) => {
    if (!iso) {
        return '';
    }
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const vehicleLine = computed(() => {
    const parts = [current.value?.vehicle_label, current.value?.vin];
    return parts.filter(Boolean).join(' · ');
});

const historyMeta = (event) => [event.tire_barcode, formatDate(event.moved_at)].filter(Boolean).join(' · ');
</script>
<template>
<div>
    <div
        class="fixed inset-0 z-[1006] bg-black/30"
        @click="emit('close')" />
    <aside class="fixed inset-y-0 right-0 z-[1007] w-full max-w-md flex flex-col shadow-2xl bg-surface">
        <div
            v-if="current"
            class="flex flex-col h-full">
            <div class="flex items-start justify-between px-6 py-5 border-b border-line shrink-0">
                <div>
                    <div class="flex items-center gap-2.5 mb-1 flex-wrap">
                        <span class="font-mono font-semibold text-lg">{{ current.barcode }}</span>
                        <StatusChip
                            v-if="current.season"
                            :color="seasonColor(current.season)">{{ $t('label.' + seasonLabel) }}</StatusChip>
                        <StatusChip :color="current.is_stored ? 'success' : 'neutral'">
                            {{ current.is_stored ? $t('label.in_storage') : $t('label.on_vehicle') }}
                        </StatusChip>
                    </div>
                    <p class="font-display font-semibold text-base text-ink">{{ current.customer_name }}</p>
                    <p class="text-xs text-ink-light font-mono">{{ vehicleLine }}</p>
                </div>
                <button
                    class="p-1.5 rounded-md hover:bg-black/5 -mr-1 text-ink-light"
                    :aria-label="$t('actions.cancel')"
                    @click="emit('close')">
                    <v-icon
                        icon="mdi-close"
                        size="18" />
                </button>
            </div>

            <div class="border-t border-line px-6 py-4 flex gap-3 shrink-0 order-last">
                <a
                    class="flex-1 py-2.5 rounded-lg text-sm font-semibold border border-line text-center no-underline text-ink hover:border-accent"
                    :href="'/tire-sets/' + current.hash + '/labels'"
                    target="_blank">{{ $t('label.print_labels') }}</a>
            </div>

            <div class="flex-1 overflow-y-auto px-6 py-5 space-y-6">
                <div
                    v-if="current.location_type === 'bin'"
                    class="rounded-xl border border-line bg-canvas p-4 flex items-center justify-between">
                    <div>
                        <p class="text-xs font-semibold uppercase tracking-wider mb-0.5 text-ink-light">{{ $t('label.location') }}</p>
                        <p class="font-mono font-semibold text-xl">{{ current.storage_name }} {{ current.location_label }}</p>
                    </div>
                    <v-icon
                        icon="mdi-warehouse"
                        size="30"
                        class="text-accent" />
                </div>

                <div>
                    <h4 class="text-xs font-semibold uppercase tracking-wider mb-3 text-ink-light">{{ $t('label.set_details') }}</h4>
                    <dl class="space-y-2.5 text-sm">
                        <div class="flex justify-between">
                            <dt class="text-ink-light">{{ $t('label.tires') }}</dt>
                            <dd class="font-medium text-right">{{ current.tire_label || current.size }}</dd>
                        </div>
                        <div class="flex justify-between">
                            <dt class="text-ink-light">{{ $t('label.quantity') }}</dt>
                            <dd class="font-medium">{{ current.quantity }}</dd>
                        </div>
                        <div
                            v-if="current.last_stored_at"
                            class="flex justify-between">
                            <dt class="text-ink-light">{{ $t('label.checked_in') }}</dt>
                            <dd class="font-medium">{{ formatDate(current.last_stored_at) }}</dd>
                        </div>
                        <div
                            v-if="current.description"
                            class="flex justify-between">
                            <dt class="text-ink-light">{{ $t('label.description') }}</dt>
                            <dd class="font-medium text-right max-w-[220px]">{{ current.description }}</dd>
                        </div>
                    </dl>
                </div>

                <div>
                    <h4 class="text-xs font-semibold uppercase tracking-wider mb-3 text-ink-light">{{ $t('label.tires') }}</h4>
                    <div class="space-y-2">
                        <div
                            v-for="tire in current.tires"
                            :key="tire.barcode"
                            class="flex items-center justify-between rounded-lg border border-line px-3 py-2 text-sm">
                            <span class="flex items-center gap-2 min-w-0">
                                <span class="font-mono text-xs rounded px-1.5 py-0.5 bg-canvas border border-line shrink-0">{{ tire.position }}</span>
                                <span class="font-mono text-xs truncate">{{ tire.barcode }}</span>
                            </span>
                            <span class="text-xs text-ink-light font-mono shrink-0">{{ tire.location ?? $t('label.on_vehicle') }}</span>
                        </div>
                    </div>
                </div>

                <div>
                    <div class="flex items-center justify-between mb-3">
                        <h4 class="text-xs font-semibold uppercase tracking-wider text-ink-light">{{ $t('label.photos') }}</h4>
                        <button
                            class="text-xs font-semibold text-accent hover:underline disabled:opacity-50"
                            :disabled="uploadingPhoto"
                            @click="pickPhoto">
                            {{ $t('label.add_photo') }}
                        </button>
                    </div>
                    <input
                        ref="photoInput"
                        type="file"
                        accept="image/*"
                        class="hidden"
                        @change="onPhotoChosen">
                    <div
                        v-if="current.photos?.length"
                        class="grid grid-cols-3 gap-2">
                        <div
                            v-for="photo in current.photos"
                            :key="photo.hash"
                            class="relative group">
                            <a
                                :href="photo.url"
                                target="_blank">
                                <img
                                    :src="photo.url"
                                    :alt="photo.filename"
                                    class="aspect-square object-cover rounded-lg border border-line w-full">
                            </a>
                            <button
                                class="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white hidden group-hover:flex items-center justify-center"
                                :aria-label="$t('actions.cancel')"
                                @click="removePhoto(photo)">
                                <v-icon
                                    icon="mdi-close"
                                    size="12" />
                            </button>
                        </div>
                    </div>
                    <p
                        v-else
                        class="text-sm text-ink-light">{{ $t('label.no_photos') }}</p>
                </div>

                <div>
                    <h4 class="text-xs font-semibold uppercase tracking-wider mb-3 text-ink-light">{{ $t('label.notes') }}</h4>
                    <div
                        v-if="current.notes?.length"
                        class="space-y-2.5 mb-4">
                        <div
                            v-for="note in current.notes"
                            :key="note.hash"
                            class="rounded-lg border border-line bg-canvas px-3 py-2.5 group">
                            <div class="flex items-start justify-between gap-2">
                                <p class="text-sm whitespace-pre-line">{{ note.message }}</p>
                                <button
                                    class="opacity-0 group-hover:opacity-100 text-ink-light hover:text-error shrink-0"
                                    :aria-label="$t('actions.cancel')"
                                    @click="removeNote(note)">
                                    <v-icon
                                        icon="mdi-close"
                                        size="14" />
                                </button>
                            </div>
                            <p class="text-xs text-ink-light mt-1.5">
                                {{ noteMeta(note) }}
                                <span
                                    v-if="note.tire_barcode"
                                    class="font-mono ml-1 rounded px-1 py-0.5 bg-surface border border-line">{{ note.tire_barcode }}</span>
                            </p>
                        </div>
                    </div>
                    <p
                        v-else
                        class="text-sm text-ink-light mb-4">{{ $t('label.no_notes') }}</p>
                    <StyledTextArea
                        v-model="noteMessage"
                        name="note_message"
                        :placeholder="$t('label.note_placeholder')" />
                    <div class="flex gap-2 mt-2 items-start">
                        <StyledDropdown
                            v-model="noteTarget"
                            clearable
                            class="flex-1"
                            :placeholder="$t('label.whole_set')"
                            :options="tireTargetOptions"
                            name="note_target" />
                        <StyledButton
                            :disabled="savingNote || !noteMessage.trim()"
                            @click="submitNote">
                            {{ $t('label.add_note') }}
                        </StyledButton>
                    </div>
                </div>

                <div v-if="current.history?.length">
                    <h4 class="text-xs font-semibold uppercase tracking-wider mb-3 text-ink-light">{{ $t('label.history') }}</h4>
                    <ol class="space-y-3 text-sm">
                        <li
                            v-for="(event, index) in current.history"
                            :key="index"
                            class="flex gap-3">
                            <span
                                class="mt-1 w-2 h-2 rounded-full shrink-0"
                                :class="event.to_customer ? 'bg-accent' : 'bg-success'" />
                            <div>
                                <p class="font-medium">
                                    {{ event.to_customer ? $t('label.returned_to_customer') : $t('label.racked') }}
                                    <span
                                        v-if="event.location_label"
                                        class="font-mono text-xs">{{ event.location_label }}</span>
                                </p>
                                <p class="text-xs text-ink-light font-mono">{{ historyMeta(event) }}</p>
                            </div>
                        </li>
                    </ol>
                </div>
            </div>
        </div>
    </aside>
</div>
</template>
